import { useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RightOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import TrendLine from "@/components/shared/Trend_Line";
import { formatCurrency, mapExpenseTotals } from "@/utils/finance_utils";
import {
  fetchExpenseByAny,
  fetchExpenseTotals,
  selectExpenseTotals,
} from "@/store/redux/slices/finance_management/expense_slice";
import { getOrganizationId } from "@/utils/token_functions";

const PriceCards = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const expenseTotals = useSelector(selectExpenseTotals);

  useEffect(() => {
    const fetchFinancialData = async () => {
      try {
        const organizationID = getOrganizationId();

        if (!organizationID) {
          console.error(
            "No organization ID available - cannot fetch financial data"
          );
          return;
        }

        await Promise.all([
          dispatch(
            fetchExpenseByAny({
              key: "organizationID",
              value: organizationID,
            })
          ).unwrap(),
          dispatch(fetchExpenseTotals(organizationID)).unwrap(),
        ]);
      } catch (error) {
        console.error("Error fetching financial data in PriceCards:", error);
      }
    };

    fetchFinancialData();
  }, [dispatch]);

  const {
    totalOutflows,
    totalInflows,
    currentBalance,
    totalDonations: totalDonationsAmount,
    trendData: financialTrendData,
    fundsPercentChange,
    expensesPercentChange,
    donationsPercentChange,
  } = useMemo(() => mapExpenseTotals(expenseTotals), [expenseTotals]);

  const formatTrendPct = (value) => `${Math.abs(Number(value) || 0)}%`;
  const trendArrow = (value) => ((Number(value) || 0) >= 0 ? "↑" : "↓");

  return (
    <div className="grid-cols-6 gap-4 mb-5 lg:grid">
      <div className="p-2 lg:p-0 mb-4 lg:mb-0 bg-white rounded-xl border-2 lg:border-none border-[#eee]">
        <div className="flex items-start">
          <div
            onClick={() => navigate("/dashboard/financial/account")}
            className="w-full lg:border-2 border-[#eee] lg:p-2 rounded-xl">
            <h3 className="mb-2 text-small-xs text-darkGrey">
              CURRENT BALANCE
            </h3>
            <div className="mb-4 font-bold heading-large text-darkGrey">
              {formatCurrency(currentBalance)}
            </div>
            <button className="flex items-center px-4 py-2 text-black rounded-full bg-secondary text-small-xs">
              VIEW MORE
              <span className="ml-2">
                <RightOutlined />
              </span>
            </button>
          </div>

          <div
            onClick={() => navigate("/dashboard/financial/donations")}
            className="w-full lg:hidden">
            <h3 className="mb-2 text-small-xs text-darkGrey">ALL DONATIONS</h3>
            <div className="mb-4 font-bold heading-large text-darkGrey">
              {formatCurrency(totalDonationsAmount)}
            </div>
            <div className="flex items-center">
              <div
                className={`px-3 py-1 text-white rounded-full text-small-xs ${
                  donationsPercentChange >= 0 ? "bg-primary" : "bg-danger"
                }`}>
                <span className="preserve-text-white">
                  {trendArrow(donationsPercentChange)}
                </span>
                <span className="preserve-text-white">
                  {formatTrendPct(donationsPercentChange)}
                </span>
              </div>
              <span className="ml-2 text-small-xs text-darkGrey">
                from last month
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:col-span-4">
        <div className="p-2 bg-white rounded-xl border-2 border-[#eee] lg:flex flex-row-reverse items-center lg:gap-2">
          <div className="h-16 bg-secondary rounded-lg mb-2">
            <TrendLine
              data={financialTrendData.inflows}
              color="#00BA07"
              type="inflow"
            />
          </div>
          <div>
            <h3 className="mb-2 text-small-xs text-darkGrey">TOTAL INFLOWS</h3>
            <div className="mb-4 font-bold heading-large text-darkGrey">
              {formatCurrency(totalInflows)}
            </div>
            <div className="flex items-center">
              <div
                className={`flex items-center gap-1 px-3 py-1 text-white rounded-full text-small-xs ${
                  fundsPercentChange >= 0 ? "bg-green" : "bg-danger"
                }`}>
                <span className="preserve-text-white">
                  {trendArrow(fundsPercentChange)}
                </span>
                <span className="preserve-text-white">
                  {formatTrendPct(fundsPercentChange)}
                </span>
              </div>
              <p className="ml-2 text-small-xs text-darkGrey">
                from last month
              </p>
            </div>
          </div>
        </div>

        <div className="p-2 bg-white rounded-xl border-2 border-[#eee] lg:flex flex-row-reverse items-center lg:gap-2">
          <div className="h-16 bg-secondary rounded-lg mb-2">
            <TrendLine
              data={financialTrendData.outflows}
              color="#FF0000"
              type="outflow"
            />
          </div>
          <div>
            <h3 className="mb-2 text-small-xs text-darkGrey">TOTAL OUTFLOWS</h3>
            <div className="mb-4 font-bold heading-large text-darkGrey">
              {formatCurrency(totalOutflows)}
            </div>
            <div className="flex items-center">
              <div
                className={`px-3 py-1 text-white rounded-full text-small-xs ${
                  expensesPercentChange >= 0 ? "bg-danger" : "bg-green"
                }`}>
                <span className="preserve-text-white">
                  {trendArrow(expensesPercentChange)}
                </span>
                <span className="preserve-text-white">
                  {formatTrendPct(expensesPercentChange)}
                </span>
              </div>
              <span className="ml-2 text-small-xs text-darkGrey">
                from last month
              </span>
            </div>
          </div>
        </div>
      </div>

      <div
        onClick={() => navigate("/dashboard/financial/donations")}
        className="w-full hidden lg:block border-2 border-[#eee] lg:p-2 rounded-xl">
        <h3 className="mb-2 text-small-xs text-darkGrey">ALL DONATIONS</h3>
        <div className="mb-4 font-bold heading-large text-darkGrey">
          {formatCurrency(totalDonationsAmount)}
        </div>
        <div className="flex items-center">
          <div
            className={`px-3 py-1 text-white rounded-full text-small-xs ${
              donationsPercentChange >= 0 ? "bg-primary" : "bg-danger"
            }`}>
            <span className="preserve-text-white">
              {trendArrow(donationsPercentChange)}
            </span>
            <span className="preserve-text-white">
              {formatTrendPct(donationsPercentChange)}
            </span>
          </div>
          <span className="ml-2 text-small-xs text-darkGrey">
            from last month
          </span>
        </div>
      </div>
    </div>
  );
};

export default PriceCards;
